import { useMemo } from "react";

import { CurrencyFormatter } from "@equidefi/i18n";
import { determineStripePaymentLimit } from "@equidefi/shared";
import { MAXIMUM_STRIPE_AMOUNT } from "@equidefi/shared/constants/payments";
import type { DTO } from "@equidefi/shared/types";

export type PaymentOfferingSetting = Pick<
  DTO.OfferingSetting,
  "stripe_enabled" | "check_enabled" | "crypto_enabled" | "stripe_wire_enabled" | "max_cc_amount"
>;

type PlatformSettings = {
  ach_disabled?: boolean | null;
  ach_message?: string | null;
};

interface UsePaymentAvailabilityParams {
  offering: { settings?: PaymentOfferingSetting | null } | null | undefined;
  investmentAmount: number | null | undefined;
  platformSettings: PlatformSettings | null | undefined;
}

type StripeLimits = ReturnType<ReturnType<typeof determineStripePaymentLimit>>;

export interface UsePaymentAvailabilityReturn {
  /** Raw limit result from determineStripePaymentLimit */
  stripeLimits: StripeLimits;
  /** True when the per-offering CC cap is exceeded (cards disabled, ACH still available) */
  isOverOfferingCcLimit: boolean;
  /** True when the global transaction cap ($110k) is exceeded (cards AND ACH disabled) */
  isOverTransactionLimit: boolean;
  /** Convenience: isOverOfferingCcLimit || isOverTransactionLimit */
  isOverAnyLimit: boolean;
  /** True if Stripe is enabled on this offering (controls whether the Stripe section renders) */
  isStripeAvailable: boolean;
  /** True if credit card payments are specifically available */
  isCardAvailable: boolean;
  /** User-facing message explaining why Stripe/CC is unavailable, or null */
  stripeDisabledMessage: string | null;
  /** True if ACH is disabled at the platform level */
  isAchDisabled: boolean;
  /** Platform-level message shown when ACH is disabled, or null */
  achDisabledMessage: string | null;
  /** True if crypto payments are enabled for this offering */
  isCryptoAvailable: boolean;
  /** True if check payments are enabled for this offering */
  isCheckAvailable: boolean;
  /** True if manual wire transfers are available (hidden when Stripe wire is enabled) */
  isWireAvailable: boolean;
  /** True if Stripe-managed wire transfers are enabled */
  isStripeWireAvailable: boolean;
}

export const usePaymentAvailability = ({
  offering,
  investmentAmount,
  platformSettings,
}: UsePaymentAvailabilityParams): UsePaymentAvailabilityReturn => {
  const settings = offering?.settings;
  const amount = investmentAmount ?? 0;

  const stripeLimits = useMemo(
    () => determineStripePaymentLimit({ creditCardLimit: settings?.max_cc_amount })(amount),
    [settings?.max_cc_amount, amount],
  );

  const isOverOfferingCcLimit = stripeLimits.isOverCreditCardLimit;
  const isOverTransactionLimit = stripeLimits.isOverTransactionLimit;

  const isOverAnyLimit = isOverOfferingCcLimit || isOverTransactionLimit;

  const isStripeAvailable = !!settings?.stripe_enabled;
  const isCardAvailable = isStripeAvailable && !isOverAnyLimit;

  const stripeDisabledMessage = useMemo(() => {
    if (!isStripeAvailable) return null;
    if (!isOverAnyLimit) return null;

    if (stripeLimits.isOverCreditCardLimit) {
      return `Credit Card payments are unavailable for investments larger than ${CurrencyFormatter.format(stripeLimits.creditCardLimit ?? stripeLimits.transactionLimit)}.`;
    }

    if (stripeLimits.isOverTransactionLimit) {
      return `Credit Card and ACH payments are unavailable for investments larger than ${CurrencyFormatter.format(stripeLimits.transactionLimit)}.`;
    }

    return null;
  }, [stripeLimits, isOverAnyLimit]);

  const isAchDisabled = !!platformSettings?.ach_disabled;
  const achDisabledMessage = platformSettings?.ach_message ?? null;

  const isCryptoAvailable = !!settings?.crypto_enabled;
  const isCheckAvailable = !!settings?.check_enabled;
  const isStripeWireAvailable = !!settings?.stripe_wire_enabled && isStripeAvailable;
  const isWireAvailable = !isStripeWireAvailable;

  return {
    stripeLimits,
    isOverOfferingCcLimit,
    isOverTransactionLimit,
    isOverAnyLimit,
    isStripeAvailable,
    isCardAvailable,
    stripeDisabledMessage,
    isAchDisabled,
    achDisabledMessage,
    isCryptoAvailable,
    isCheckAvailable,
    isWireAvailable,
    isStripeWireAvailable,
  };
};
